import React, { Suspense } from "react";
import { useTenant } from "../contexts/TenantContext";

// Lazy load components
const PrivacyHeader = React.lazy(() => import("../components/layout/privacy/PrivacyHeader"));
const InformationCollected = React.lazy(
  () => import("../components/layout/privacy/InformationCollected")
);
const CompanyUseSection = React.lazy(
  () => import("../components/layout/privacy/CompanyUseSection")
);
const SharingInfoSection = React.lazy(
  () => import("../components/layout/privacy/SharingInfoSection")
);
const RightsChoicesSection = React.lazy(
  () => import("../components/layout/privacy/RightsChoicesSection")
);
const SecurityRetentionSection = React.lazy(
  () => import("../components/layout/privacy/SecurityRetentionSection")
);
const MinorsSection = React.lazy(() => import("../components/layout/privacy/MinorsSection"));
const InternationalSection = React.lazy(
  () => import("../components/layout/privacy/InternationalSection")
);
const PrivacyRightsIntroSection = React.lazy(
  () => import("../components/layout/privacy/PrivacyRightsIntroSection")
);
const DataCollectionDetailsSection = React.lazy(
  () => import("../components/layout/privacy/DataCollectionDetailsSection")
);
const PersonalInformationUseSection = React.lazy(
  () => import("../components/layout/privacy/PersonalInformationUseSection")
);
const PersonalInformationDisclosureSection = React.lazy(
  () => import("../components/layout/privacy/PersonalInformationDisclosureSection")
);
const AccessRightsSection = React.lazy(
  () => import("../components/layout/privacy/AccessRightsSection")
);
const DeletionRequestSection = React.lazy(
  () => import("../components/layout/privacy/DeletionRequestSection")
);
const ExercisingRightsSection = React.lazy(
  () => import("../components/layout/privacy/ExercisingRightsSection")
);
const ResponseTimingSection = React.lazy(
  () => import("../components/layout/privacy/ResponseTimingSection")
);
const ChangesSection = React.lazy(() => import("../components/layout/privacy/ChangesSection"));
const DoNotTrackSection = React.lazy(
  () => import("../components/layout/privacy/DoNotTrackSection")
);
const AdditionalInfoSection = React.lazy(
  () => import("../components/layout/privacy/AdditionalInfoSection")
);

const LoadingFallback = () => (
  <div className="animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
    <div className="h-4 bg-gray-200 rounded w-5/6"></div>
  </div>
);

const PrivacyPage: React.FC = () => {
  const { config } = useTenant();
  const orgName = config?.orgData?.org_name || "";
  const orgEmail = config?.orgData?.email || "";
  const orgPhone = config?.orgData?.org_phone || "";
  const orgAddress = config?.orgData?.client_address;
  const formattedAddress = orgAddress
    ? `${orgAddress.street}, ${orgAddress.city}, ${orgAddress.state} ${orgAddress.zip}`
    : "";
  const domain =
    (typeof window !== "undefined" && window.location.hostname) ||
    config?.domain?.primary ||
    "speculo.ai";
  const primaryColor = config?.branding?.colors.primary;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col max-w-4xl mx-auto gap-2 lg:gap-5">
        <Suspense fallback={<LoadingFallback />}>
          {/* 1. Privacy Policy & Overview */}
          <PrivacyHeader
            domain={domain}
            orgName={orgName}
            primaryColor={primaryColor}
          />

          {/* 2. Information Collection */}
          <InformationCollected />

          {/* 4-5. Company Use & Legal Basis */}
          <CompanyUseSection />

          {/* 6. Sharing Information */}
          <SharingInfoSection />

          {/* 7. Rights and Choices */}
          <RightsChoicesSection
            orgEmail={orgEmail}
            primaryColor={primaryColor}
          />

          {/* 8-9-10. Security, Retention, Links */}
          <SecurityRetentionSection />

          {/* 11. Minors */}
          <MinorsSection />

          {/* 12-13. International Data & EEA */}
          <InternationalSection
            orgEmail={orgEmail}
            primaryColor={primaryColor}
          />

          {/* 14. California Residents sections */}
          <PrivacyRightsIntroSection />
          <DataCollectionDetailsSection />
          <PersonalInformationUseSection />
          <PersonalInformationDisclosureSection
            orgEmail={orgEmail}
            primaryColor={primaryColor}
          />
          <AccessRightsSection
            orgEmail={orgEmail}
            orgPhone={orgPhone}
            formattedAddress={formattedAddress}
            primaryColor={primaryColor}
          />
          <DeletionRequestSection />
          <ExercisingRightsSection
            orgEmail={orgEmail}
            orgPhone={orgPhone}
            formattedAddress={formattedAddress}
            primaryColor={primaryColor}
          />
          <ResponseTimingSection
            orgEmail={orgEmail}
            primaryColor={primaryColor}
          />

          {/* 15. Changes */}
          <ChangesSection />

          {/* 16. Do Not Track */}
          <DoNotTrackSection />

          {/* 17-22. Additional Sections */}
          <AdditionalInfoSection
            orgEmail={orgEmail}
            formattedAddress={formattedAddress}
            primaryColor={primaryColor}
          />
        </Suspense>
      </div>
    </div>
  );
};

export default PrivacyPage;
